import { createAsyncThunk,createSlice } from "@reduxjs/toolkit"
import { PayloadAction } from "@reduxjs/toolkit"
import axios from "axios"

import { FormCategoryOrder } from "../../../utils/evaluation-category-order"

// ─── Types ────────────────────────────────────────────────────────────────────

export interface WorkerAcknowledgementItem {
	instanceFormId: number
	instanceId: number
	programId: number
	programName: string
	workerId: string
	workerName: string
	formName: string
	score: number | null
	metTarget: boolean | null
	eligibleDate: string
	deadlineDate: string | null
	instanceStatusCode: string
	formStatusCode: string
	isAcknowledged: boolean
	acknowledgedDate: string | null
	comment: string | null
	// GAT-7805 — set once the worker has declined this form; kept as history
	// after a later Acknowledge, so check formStatusCode for current status.
	declineReason: string | null
	declinedDate: string | null
}

export interface WorkerProgramItem {
	instanceId: number
	evaluationProgramId: number
	programName: string
	statusId: number
	statusCode: string
	statusName: string
	eligibleDate: string | null
	deadlineDate: string | null
	startedDate: string | null
	completedDate: string | null
	outcomeDisplay: string | null
}

export interface WorkerFormSummary {
	// Null unless a manager-side/worker-side answer set exists for that form
	// (USER_ONLY|BOTH for user*, WORKER_ONLY|BOTH for worker*). Neither side is
	// a "primary" — there's no default to fall back to. workerInstanceFormId is
	// the id the worker app must use to act on its OWN answer set.
	userInstanceFormId: number | null
	workerInstanceFormId: number | null
	programFormId: number
	evaluationFormId: number
	formName: string
	displayOrder: number
	userStatusId: number | null
	userStatusCode: string | null
	userStatusName: string | null
	workerStatusId: number | null
	workerStatusCode: string | null
	workerStatusName: string | null
	userScore: number | null
	workerScore: number | null
	targetValue: number | null
	metTarget: boolean | null
	// Acknowledgement progress (GAT-7449 cross-acknowledgement). Each row (user's
	// and worker's own) is held to the full set of ACKNOWLEDGE steps on the
	// template independently, so completion is tracked per row; the required
	// count is template-level and identical for both. All null when the form
	// has no acknowledgement workflow attached.
	userAcknowledgementsCompleted: number | null
	workerAcknowledgementsCompleted: number | null
	acknowledgementsTotal: number | null
	hasWorkerAcknowledgedManagerRow: boolean | null
	hasWorkerAcknowledgedOwnRow: boolean | null
	// Per-row decline history (GAT-7807). Same history semantics as
	// WorkerFormDetail's: kept after a later Acknowledge, so check the row's
	// status to know whether it's still DECLINED.
	userDeclineReason: string | null
	userDeclinedDate: string | null
	workerDeclineReason: string | null
	workerDeclinedDate: string | null
	// Shared-evaluations (GAT-7449) per-form fill config. USER_ONLY (default)
	// behaves exactly as before these fields existed.
	fillModeCode?: string | null
	outcome: {
		outcomeId: number
		outcomeTypeCode: string
		outcomeTypeName: string
		skillId: number | null
		skillName: string | null
		jobId: number | null
		jobName: string | null
		targetSkillLevelId: number | null
	} | null
}

export interface WorkerAdditionalField {
	additionalFieldId: number
	label: string
	fieldTypeCode: string
	isRequired: boolean
	displayOrder: number
	responseValue: string | null
}

export interface WorkerProgramDetail {
	instanceId: number
	evaluationProgramId: number
	companyId: number
	programName: string
	workerId: string
	workerFirstName: string
	workerLastName: string
	statusId: number
	statusCode: string
	statusName: string
	eligibleDate: string | null
	deadlineDate: string | null
	startedDate: string | null
	completedDate: string | null
	forms: WorkerFormSummary[]
	additionalFields: WorkerAdditionalField[]
	// IANA zone of the worker's site (GAT-7811), for showing signature times
	// in site-local time. null when the location's zone isn't known.
	locationTimeZone?: string | null
}

export interface WorkerQuestionOption {
	id: number
	optionText: string
	optionValue: string
	displayOrder: number
}

export interface WorkerFormQuestion {
	questionId: number
	questionText: string
	helpText: string | null
	typeCode: string
	categoryCode: string | null
	categoryName: string | null
	displayOrder: number
	isRequired: boolean
	minValue: number | null
	maxValue: number | null
	minLength: number | null
	maxLength: number | null
	options: WorkerQuestionOption[]
	// The answer for the set this GetFormDetails call resolves to (the manager's
	// authoritative set, or — when called with a workerInstanceFormId — the
	// worker's own set). The counterpart's answers arrive in
	// WorkerFormDetail.counterpartResponses, keyed by questionId.
	responseValue: string | null
	selectedOptionId: number | null
}

// The other respondent's answers, for the side-by-side compare view. Delivered
// as a block (not inline per question); null on USER_ONLY forms or before the
// caller's own side has been submitted.
export interface CounterpartAnswer {
	questionId: number
	responseValue: string | null
	selectedOptionId: number | null
}

export interface CounterpartResponses {
	respondentType: "USER" | "WORKER"
	fillStatusCode: string
	submittedDate: string | null
	answers: CounterpartAnswer[]
}

export interface WorkerAckStep {
	stepId: number
	stepOrder: number
	assigneeTypeCode: "USER_ROLE" | "WORKER"
	assigneeRoleId: number | null
	assigneeRoleName: string | null
	stepActionCode: string
	isAcknowledged: boolean
	acknowledgedByUserId: number | null
	acknowledgedByWorkerId: string | null
	acknowledgedByName: string | null
	acknowledgementDate: string | null
	comment: string | null
	// E-signature (frozen when the form was issued). signatureImageUrl is a
	// worker-token-protected path relative to the API base, null until signed.
	requiresSignature?: boolean
	signatureImageUrl?: string | null
}

export interface WorkerAckWorkflow {
	acknowledgementTemplateId: number
	steps: WorkerAckStep[]
}

export interface WorkerFormOutcome {
	outcomeId: number
	outcomeTypeCode: string
	outcomeTypeName: string
	skillId: number | null
	skillName: string | null
	jobId: number | null
	jobName: string | null
	targetSkillLevelId: number | null
	currentSkillLevelId: number | null
}

export interface WorkerFormDetail {
	instanceFormId: number
	// Categories used on the form, in the order set in the form builder
	categories?: FormCategoryOrder[] | null
	instanceId: number
	evaluationFormId: number
	companyId: number
	formName: string
	// Both parties' status, always returned regardless of which side this
	// instanceFormId/questions belong to. Use editableRespondentType (see
	// getRespondentStatus in util/respondent-status.ts) to know which pair
	// corresponds to the answer set actually loaded here.
	userStatusId: number | null
	userStatusCode: string | null
	userStatusName: string | null
	workerStatusId: number | null
	workerStatusCode: string | null
	workerStatusName: string | null
	score: number | null
	targetValue: number | null
	questions: WorkerFormQuestion[]
	outcome: WorkerFormOutcome | null
	acknowledgementWorkflow: WorkerAckWorkflow | null
	// Shared-evaluations (GAT-7449). "BOTH" switches the acknowledge screen to
	// the side-by-side compare view.
	fillModeCode?: string | null
	fillOrderCode?: string | null
	// Which set is editable for this specific instanceFormId: "USER" = the
	// manager's (read-only for the worker); "WORKER" = the worker's own set.
	editableRespondentType?: "USER" | "WORKER"
	// The other respondent's answers for compare. Null on USER_ONLY forms or
	// before the caller's own side has been submitted.
	counterpartResponses?: CounterpartResponses | null
	// GAT-7805 — set once the worker has declined this row's WORKER step. Not
	// cleared by a later Acknowledge (history), so a non-null declineReason
	// doesn't mean the row is still DECLINED — check its status for that.
	declineReason?: string | null
	declinedDate?: string | null
	declinedByWorkerId?: string | null
}

// Worker's own SaveFormAnswers response. Never carries score/outcome — those are
// driven only by the manager's authoritative set.
export interface WorkerSaveResponse {
	instanceFormId: number
	instanceStatusCode: string
	formStatusCode: string
}

export interface WorkerSaveFormAnswersPayload {
	// Always the worker's own workerInstanceFormId, never the manager's id.
	instanceFormId: number
	isFinal: boolean
	answers: {
		questionId: number
		selectedOptionId: number | null
		textValue?: string | null
		responseValue?: string | null
	}[]
}

export interface WorkerAcknowledgePayload {
	instanceFormId: number
	comment?: string | null
	// Bare base64 PNG (no data: prefix). Sent only when the worker's step
	// requires a signature; omitted otherwise.
	signatureImageBase64?: string
}

export interface WorkerDeclinePayload {
	instanceFormId: number
	reason: string
}

// ─── State ────────────────────────────────────────────────────────────────────

interface WorkerEvaluationsState {
	isPageOpen: boolean
	currentView: "list" | "programDetail" | "formDetail"
	// Fetched purely to compute instanceIdsNeedingAck for the Programs list's
	// "needs you" bucketing (classifyProgramAttention) — not for a standalone
	// acknowledgements list; the guided per-form banner in FormDetailView is
	// the only acknowledgement UI now.
	myAcknowledgements: {
		status: "idle" | "pending" | "fulfilled" | "rejected"
		data: WorkerAcknowledgementItem[]
		totalCount: number
	}
	myPrograms: {
		status: "idle" | "pending" | "fulfilled" | "rejected"
		data: WorkerProgramItem[]
	}
	// Per-program forms breakdown, fetched only for programs whose aggregate
	// statusCode is ambiguous (PENDING/IN_PROGRESS/ACKNOWLEDGEMENT_PENDING) —
	// lets classifyProgramAttention resolve a self-ack-blocked own row
	// (GAT-7449) precisely instead of guessing from the aggregate status.
	// Reset on every fresh fetchMyPrograms rather than kept indefinitely: it's
	// live per-form status that changes on every fill/submit/acknowledge, so a
	// stale cached snapshot would keep a resolved program bucketed wrong.
	programAttentionForms: Record<
		number,
		{
			status: "idle" | "pending" | "fulfilled" | "rejected"
			forms: WorkerFormSummary[]
		}
	>
	programDetail: {
		status: "idle" | "pending" | "fulfilled" | "rejected"
		data: WorkerProgramDetail | null
		instanceId: number | null
	}
	formDetail: {
		status: "idle" | "pending" | "fulfilled" | "rejected"
		data: WorkerFormDetail | null
		instanceFormId: number | null
		// The other row on a BOTH-mode form (GAT-7449 cross-acknowledgement) —
		// e.g. the manager's row when `data` holds the worker's own row.
		// Fetched in parallel so both rows' status/acknowledge action can
		// render together on the one form-detail screen.
		counterpartInstanceFormId: number | null
		counterpartData: WorkerFormDetail | null
		counterpartStatus: "idle" | "pending" | "fulfilled" | "rejected"
	}
	acknowledgeFormStatus: "idle" | "pending" | "fulfilled" | "rejected"
	acknowledgeFormError: string | null
	counterpartAcknowledgeFormStatus: "idle" | "pending" | "fulfilled" | "rejected"
	counterpartAcknowledgeFormError: string | null
	// The last successful acknowledgement carried an e-signature — picks the
	// success toast's wording. Kept here (not in the view) because the form
	// view remounts while the acknowledged row is re-fetched.
	lastAcknowledgeWasSigned: boolean
	// Only ever targets the manager's row (GAT-7807), so unlike acknowledge it
	// needs no primary/counterpart split.
	declineFormStatus: "idle" | "pending" | "fulfilled" | "rejected"
	declineFormError: string | null
	// Self-Evaluations tab — the worker's own answer sets. Kept separate from the
	// Acknowledge tab's programDetail/formDetail so the two tabs never clobber
	// each other while both are mounted.
	selfEval: {
		programDetail: {
			status: "idle" | "pending" | "fulfilled" | "rejected"
			data: WorkerProgramDetail | null
			instanceId: number | null
		}
		formDetail: {
			status: "idle" | "pending" | "fulfilled" | "rejected"
			data: WorkerFormDetail | null
			instanceFormId: number | null
		}
		saveStatus: "idle" | "pending" | "fulfilled" | "rejected"
		saveError: string | null
	}
}

const initialState: WorkerEvaluationsState = {
	isPageOpen: false,
	currentView: "list",
	myAcknowledgements: {
		status: "idle",
		data: [],
		totalCount: 0,
	},
	myPrograms: {
		status: "idle",
		data: [],
	},
	programAttentionForms: {},
	programDetail: {
		status: "idle",
		data: null,
		instanceId: null,
	},
	formDetail: {
		status: "idle",
		data: null,
		instanceFormId: null,
		counterpartInstanceFormId: null,
		counterpartData: null,
		counterpartStatus: "idle",
	},
	acknowledgeFormStatus: "idle",
	acknowledgeFormError: null,
	counterpartAcknowledgeFormStatus: "idle",
	counterpartAcknowledgeFormError: null,
	lastAcknowledgeWasSigned: false,
	declineFormStatus: "idle",
	declineFormError: null,
	selfEval: {
		programDetail: {
			status: "idle",
			data: null,
			instanceId: null,
		},
		formDetail: {
			status: "idle",
			data: null,
			instanceFormId: null,
		},
		saveStatus: "idle",
		saveError: null,
	},
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function workerAuthHeaders() {
	return {
		"Content-Type": "application/json",
		Authorization: localStorage.getItem("worker-token") ?? "",
	}
}

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const fetchMyAcknowledgements = createAsyncThunk(
	"workerEvaluations/fetchMyAcknowledgements",
	async (isPending: boolean | undefined, thunkAPI) => {
		try {
			const instance = axios.create()
			const params: Record<string, string> = {}
			if (isPending !== undefined) {
				params.isPending = String(isPending)
			}
			const response = await instance.get(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluation/GetMyAcknowledgements`,
				{ headers: workerAuthHeaders(), params }
			)
			if (response.status === 204) {
				return thunkAPI.fulfillWithValue({ items: [], totalCount: 0 })
			}
			return thunkAPI.fulfillWithValue(response.data)
		} catch (err: any) {
			return thunkAPI.rejectWithValue(
				err?.response?.data?.message ?? "Failed to fetch acknowledgements"
			)
		}
	}
)

export const fetchMyPrograms = createAsyncThunk(
	"workerEvaluations/fetchMyPrograms",
	async (_, thunkAPI) => {
		try {
			const instance = axios.create()
			const response = await instance.get(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluation/GetMyPrograms`,
				{ headers: workerAuthHeaders() }
			)
			if (response.status === 204) {
				return thunkAPI.fulfillWithValue([])
			}
			return thunkAPI.fulfillWithValue(response.data)
		} catch (err: any) {
			return thunkAPI.rejectWithValue(
				err?.response?.data?.message ?? "Failed to fetch programs"
			)
		}
	}
)

// Forms breakdown for one program, fetched only to resolve the Programs
// list's attention bucket (classifyProgramAttention) for a program whose
// aggregate statusCode is ambiguous — kept in its own state slot so it never
// clobbers the programDetail the worker sees when they open the program.
export const fetchProgramAttentionForms = createAsyncThunk(
	"workerEvaluations/fetchProgramAttentionForms",
	async (instanceId: number, thunkAPI) => {
		try {
			const instance = axios.create()
			const response = await instance.get(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluation/GetProgramDetails/${instanceId}`,
				{ headers: workerAuthHeaders() }
			)
			return thunkAPI.fulfillWithValue({
				instanceId,
				forms: (response.data as WorkerProgramDetail | null)?.forms ?? [],
			})
		} catch (err: any) {
			return thunkAPI.rejectWithValue({
				instanceId,
				message: err?.response?.data?.message,
			})
		}
	}
)

export const fetchProgramDetail = createAsyncThunk(
	"workerEvaluations/fetchProgramDetail",
	async (instanceId: number, thunkAPI) => {
		try {
			const instance = axios.create()
			const response = await instance.get(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluation/GetProgramDetails/${instanceId}`,
				{ headers: workerAuthHeaders() }
			)
			return thunkAPI.fulfillWithValue(response.data)
		} catch (err: any) {
			return thunkAPI.rejectWithValue(
				err?.response?.data?.message ?? "Failed to fetch program details"
			)
		}
	}
)

export const fetchFormDetail = createAsyncThunk(
	"workerEvaluations/fetchFormDetail",
	async (instanceFormId: number, thunkAPI) => {
		try {
			const instance = axios.create()
			const response = await instance.get(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluation/GetFormDetails/${instanceFormId}`,
				{ headers: workerAuthHeaders() }
			)
			return thunkAPI.fulfillWithValue(response.data)
		} catch (err: any) {
			return thunkAPI.rejectWithValue(
				err?.response?.data?.message ?? "Failed to fetch form details"
			)
		}
	}
)

// Same call as fetchFormDetail, kept as a separate thunk so its
// pending/fulfilled/rejected cases can write into formDetail's counterpart
// slot without clobbering the primary row's data.
export const fetchCounterpartFormDetail = createAsyncThunk(
	"workerEvaluations/fetchCounterpartFormDetail",
	async (instanceFormId: number, thunkAPI) => {
		try {
			const instance = axios.create()
			const response = await instance.get(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluation/GetFormDetails/${instanceFormId}`,
				{ headers: workerAuthHeaders() }
			)
			return thunkAPI.fulfillWithValue(response.data)
		} catch (err: any) {
			return thunkAPI.rejectWithValue(
				err?.response?.data?.message ?? "Failed to fetch form details"
			)
		}
	}
)

// The HTTP status rides on the rejected action's meta so the signature
// dialog can tell a 409 (already acknowledged — today's snackbar handling)
// from a retryable failure; the payload stays the message string.
export const acknowledgeForm = createAsyncThunk<
	any,
	WorkerAcknowledgePayload,
	{ rejectValue: string; rejectedMeta: { status: number | null } }
>(
	"workerEvaluations/acknowledgeForm",
	async (payload, thunkAPI) => {
		try {
			const instance = axios.create()
			const response = await instance.post(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluation/AcknowledgeForm`,
				payload,
				{ headers: workerAuthHeaders() }
			)
			return thunkAPI.fulfillWithValue(response.data)
		} catch (err: any) {
			return thunkAPI.rejectWithValue(
				err?.response?.data?.message ?? "Failed to acknowledge form",
				{ status: err?.response?.status ?? null }
			)
		}
	}
)

// Declines the worker's pending acknowledgement step with a reason (GAT-7805).
// The form moves to DECLINED; a later acknowledgeForm resumes the workflow.
export const declineForm = createAsyncThunk(
	"workerEvaluations/declineForm",
	async (payload: WorkerDeclinePayload, thunkAPI) => {
		try {
			const instance = axios.create()
			const response = await instance.post(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluation/DeclineForm`,
				payload,
				{ headers: workerAuthHeaders() }
			)
			return thunkAPI.fulfillWithValue(response.data)
		} catch (err: any) {
			return thunkAPI.rejectWithValue(err?.response?.data?.message ?? null)
		}
	}
)

// ─── Self-Evaluations tab thunks ───────────────────────────────────────────────

// Program detail for the worker's own self-fill. Same endpoint as the Acknowledge
// tab, stored in the selfEval subtree so the two views stay independent.
export const fetchSelfEvalProgramDetail = createAsyncThunk(
	"workerEvaluations/fetchSelfEvalProgramDetail",
	async (instanceId: number, thunkAPI) => {
		try {
			const instance = axios.create()
			const response = await instance.get(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluation/GetProgramDetails/${instanceId}`,
				{ headers: workerAuthHeaders() }
			)
			return thunkAPI.fulfillWithValue(response.data)
		} catch (err: any) {
			return thunkAPI.rejectWithValue(
				err?.response?.data?.message ?? "Failed to fetch program details"
			)
		}
	}
)

// Form detail for the worker's OWN answer set — always called with the form's
// workerInstanceFormId (from GetProgramDetails), never the manager's id.
export const fetchSelfEvalFormDetail = createAsyncThunk(
	"workerEvaluations/fetchSelfEvalFormDetail",
	async (workerInstanceFormId: number, thunkAPI) => {
		try {
			const instance = axios.create()
			const response = await instance.get(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluation/GetFormDetails/${workerInstanceFormId}`,
				{ headers: workerAuthHeaders() }
			)
			return thunkAPI.fulfillWithValue(response.data)
		} catch (err: any) {
			return thunkAPI.rejectWithValue(
				err?.response?.data?.message ?? "Failed to fetch form details"
			)
		}
	}
)

// Worker saves/submits their own self-evaluation answers.
export const saveWorkerFormAnswers = createAsyncThunk(
	"workerEvaluations/saveWorkerFormAnswers",
	async (payload: WorkerSaveFormAnswersPayload, thunkAPI) => {
		try {
			const instance = axios.create()
			const response = await instance.post(
				`${process.env.REACT_APP_API_END_POINT}/WorkerEvaluation/SaveFormAnswers`,
				payload,
				{ headers: workerAuthHeaders() }
			)
			return thunkAPI.fulfillWithValue(response.data)
		} catch (err: any) {
			// 409 = sequencing gate (not the worker's turn yet on a USER_FIRST form).
			if (err?.response?.status === 409) {
				return thunkAPI.rejectWithValue(
					err?.response?.data?.message ??
						"It isn't your turn to fill this form yet."
				)
			}
			return thunkAPI.rejectWithValue(
				err?.response?.data?.message ?? "Failed to save answers"
			)
		}
	}
)

// ─── Slice ────────────────────────────────────────────────────────────────────

const workerEvaluationsSlice = createSlice({
	name: "workerEvaluations",
	initialState,
	reducers: {
		openEvaluationsPage(state) {
			state.isPageOpen = true
			state.currentView = "list"
		},
		closeEvaluationsPage(state) {
			state.isPageOpen = false
		},
		openFormDetail(
			state,
			action: PayloadAction<{
				instanceFormId: number
				counterpartInstanceFormId?: number | null
			}>
		) {
			state.currentView = "formDetail"
			state.formDetail.instanceFormId = action.payload.instanceFormId
			state.formDetail.data = null
			state.formDetail.status = "idle"
			state.formDetail.counterpartInstanceFormId =
				action.payload.counterpartInstanceFormId ?? null
			state.formDetail.counterpartData = null
			state.formDetail.counterpartStatus = "idle"
			state.acknowledgeFormStatus = "idle"
			state.counterpartAcknowledgeFormStatus = "idle"
			state.declineFormStatus = "idle"
			state.declineFormError = null
		},
		openProgramDetail(state, action: PayloadAction<number>) {
			state.currentView = "programDetail"
			state.programDetail.instanceId = action.payload
			state.programDetail.data = null
			state.programDetail.status = "idle"
		},
		goBackToList(state) {
			state.currentView = "list"
			state.formDetail.data = null
			state.formDetail.instanceFormId = null
			state.formDetail.counterpartData = null
			state.formDetail.counterpartInstanceFormId = null
			state.programDetail.data = null
			state.programDetail.instanceId = null
			state.acknowledgeFormStatus = "idle"
			state.counterpartAcknowledgeFormStatus = "idle"
			state.declineFormStatus = "idle"
			state.declineFormError = null
		},
		goBackToProgramDetail(state) {
			state.currentView = "programDetail"
			state.formDetail.data = null
			state.formDetail.instanceFormId = null
			state.formDetail.counterpartData = null
			state.formDetail.counterpartInstanceFormId = null
			state.acknowledgeFormStatus = "idle"
			state.counterpartAcknowledgeFormStatus = "idle"
			state.declineFormStatus = "idle"
			state.declineFormError = null
		},
		resetAcknowledgeFormStatus(state) {
			state.acknowledgeFormStatus = "idle"
			state.acknowledgeFormError = null
			state.counterpartAcknowledgeFormStatus = "idle"
			state.counterpartAcknowledgeFormError = null
			state.lastAcknowledgeWasSigned = false
		},
		resetDeclineFormStatus(state) {
			state.declineFormStatus = "idle"
			state.declineFormError = null
		},
		// Self-Evaluations tab navigation resets.
		openSelfEvalProgram(state, action: PayloadAction<number>) {
			state.selfEval.programDetail.instanceId = action.payload
			state.selfEval.programDetail.data = null
			state.selfEval.programDetail.status = "idle"
		},
		openSelfEvalForm(state, action: PayloadAction<number>) {
			state.selfEval.formDetail.instanceFormId = action.payload
			state.selfEval.formDetail.data = null
			state.selfEval.formDetail.status = "idle"
			state.selfEval.saveStatus = "idle"
			state.selfEval.saveError = null
		},
		closeSelfEvalForm(state) {
			state.selfEval.formDetail.data = null
			state.selfEval.formDetail.instanceFormId = null
			state.selfEval.formDetail.status = "idle"
			state.selfEval.saveStatus = "idle"
			state.selfEval.saveError = null
		},
		closeSelfEvalProgram(state) {
			state.selfEval.programDetail.data = null
			state.selfEval.programDetail.instanceId = null
			state.selfEval.programDetail.status = "idle"
			state.selfEval.formDetail.data = null
			state.selfEval.formDetail.instanceFormId = null
			state.selfEval.formDetail.status = "idle"
		},
		resetSelfEvalSaveStatus(state) {
			state.selfEval.saveStatus = "idle"
			state.selfEval.saveError = null
		},
	},
	extraReducers: (builder) => {
		// fetchMyAcknowledgements
		builder
			.addCase(fetchMyAcknowledgements.pending, (state) => {
				state.myAcknowledgements.status = "pending"
			})
			.addCase(fetchMyAcknowledgements.fulfilled, (state, action: any) => {
				state.myAcknowledgements.status = "fulfilled"
				state.myAcknowledgements.data = action.payload.items ?? []
				state.myAcknowledgements.totalCount = action.payload.totalCount ?? 0
			})
			.addCase(fetchMyAcknowledgements.rejected, (state) => {
				state.myAcknowledgements.status = "rejected"
			})

		// fetchMyPrograms
		builder
			.addCase(fetchMyPrograms.pending, (state) => {
				state.myPrograms.status = "pending"
				// Live per-form status (GAT-7449 gotcha) — never carry a stale
				// breakdown into a fresh list load; each program that's still
				// ambiguous re-fetches its own breakdown below.
				state.programAttentionForms = {}
			})
			.addCase(fetchMyPrograms.fulfilled, (state, action: any) => {
				state.myPrograms.status = "fulfilled"
				state.myPrograms.data = action.payload ?? []
			})
			.addCase(fetchMyPrograms.rejected, (state) => {
				state.myPrograms.status = "rejected"
			})

		// fetchProgramAttentionForms
		builder
			.addCase(fetchProgramAttentionForms.pending, (state, action) => {
				state.programAttentionForms[action.meta.arg] = {
					status: "pending",
					forms: state.programAttentionForms[action.meta.arg]?.forms ?? [],
				}
			})
			.addCase(fetchProgramAttentionForms.fulfilled, (state, action: any) => {
				state.programAttentionForms[action.payload.instanceId] = {
					status: "fulfilled",
					forms: action.payload.forms,
				}
			})
			.addCase(fetchProgramAttentionForms.rejected, (state, action: any) => {
				const instanceId = action.payload?.instanceId ?? action.meta.arg
				state.programAttentionForms[instanceId] = {
					status: "rejected",
					forms: state.programAttentionForms[instanceId]?.forms ?? [],
				}
			})

		// fetchProgramDetail
		builder
			.addCase(fetchProgramDetail.pending, (state) => {
				state.programDetail.status = "pending"
			})
			.addCase(fetchProgramDetail.fulfilled, (state, action: any) => {
				state.programDetail.status = "fulfilled"
				state.programDetail.data = action.payload

				// Keep the list view's plan status in sync (e.g. after an
				// acknowledgement transitions the instance to COMPLETED).
				const detail = action.payload as WorkerProgramDetail | null
				if (detail) {
					const program = state.myPrograms.data.find(
						(p) => p.instanceId === detail.instanceId
					)
					if (program) {
						program.statusId = detail.statusId
						program.statusCode = detail.statusCode
						program.statusName = detail.statusName
						program.startedDate = detail.startedDate
						program.completedDate = detail.completedDate
					}
				}
			})
			.addCase(fetchProgramDetail.rejected, (state) => {
				state.programDetail.status = "rejected"
			})

		// fetchFormDetail
		builder
			.addCase(fetchFormDetail.pending, (state) => {
				state.formDetail.status = "pending"
			})
			.addCase(fetchFormDetail.fulfilled, (state, action: any) => {
				state.formDetail.status = "fulfilled"
				state.formDetail.data = action.payload
			})
			.addCase(fetchFormDetail.rejected, (state) => {
				state.formDetail.status = "rejected"
			})

		// fetchCounterpartFormDetail
		builder
			.addCase(fetchCounterpartFormDetail.pending, (state) => {
				state.formDetail.counterpartStatus = "pending"
			})
			.addCase(fetchCounterpartFormDetail.fulfilled, (state, action: any) => {
				state.formDetail.counterpartStatus = "fulfilled"
				state.formDetail.counterpartData = action.payload
			})
			.addCase(fetchCounterpartFormDetail.rejected, (state) => {
				state.formDetail.counterpartStatus = "rejected"
			})

		// acknowledgeForm — instanceFormId may be either the primary row or its
		// counterpart (GAT-7449: both rows can be acknowledged from the same
		// screen), so route pending/fulfilled/rejected to the matching slot.
		builder
			.addCase(acknowledgeForm.pending, (state, action) => {
				if (action.meta.arg.instanceFormId === state.formDetail.counterpartInstanceFormId) {
					state.counterpartAcknowledgeFormStatus = "pending"
				} else {
					state.acknowledgeFormStatus = "pending"
				}
			})
			.addCase(acknowledgeForm.fulfilled, (state, action) => {
				state.lastAcknowledgeWasSigned = Boolean(
					action.meta.arg.signatureImageBase64
				)
				if (action.meta.arg.instanceFormId === state.formDetail.counterpartInstanceFormId) {
					state.counterpartAcknowledgeFormStatus = "fulfilled"
				} else {
					state.acknowledgeFormStatus = "fulfilled"
				}
			})
			.addCase(acknowledgeForm.rejected, (state, action: any) => {
				const isCounterpart =
					action.meta.arg.instanceFormId === state.formDetail.counterpartInstanceFormId
				if (isCounterpart) {
					state.counterpartAcknowledgeFormStatus = "rejected"
					state.counterpartAcknowledgeFormError =
						(action.payload as string) ?? "Failed to acknowledge form"
				} else {
					state.acknowledgeFormStatus = "rejected"
					state.acknowledgeFormError =
						(action.payload as string) ?? "Failed to acknowledge form"
				}
			})

			// declineForm
			builder
				.addCase(declineForm.pending, (state) => {
					state.declineFormStatus = "pending"
					state.declineFormError = null
				})
				.addCase(declineForm.fulfilled, (state) => {
					state.declineFormStatus = "fulfilled"
				})
				.addCase(declineForm.rejected, (state, action: any) => {
					state.declineFormStatus = "rejected"
					state.declineFormError = (action.payload as string | null) ?? null
				})

			// fetchSelfEvalProgramDetail
			builder
				.addCase(fetchSelfEvalProgramDetail.pending, (state) => {
					state.selfEval.programDetail.status = "pending"
				})
				.addCase(
					fetchSelfEvalProgramDetail.fulfilled,
					(state, action: any) => {
						state.selfEval.programDetail.status = "fulfilled"
						state.selfEval.programDetail.data = action.payload
					}
				)
				.addCase(fetchSelfEvalProgramDetail.rejected, (state) => {
					state.selfEval.programDetail.status = "rejected"
				})

			// fetchSelfEvalFormDetail
			builder
				.addCase(fetchSelfEvalFormDetail.pending, (state) => {
					state.selfEval.formDetail.status = "pending"
				})
				.addCase(fetchSelfEvalFormDetail.fulfilled, (state, action: any) => {
					state.selfEval.formDetail.status = "fulfilled"
					state.selfEval.formDetail.data = action.payload
				})
				.addCase(fetchSelfEvalFormDetail.rejected, (state) => {
					state.selfEval.formDetail.status = "rejected"
				})

			// saveWorkerFormAnswers
			builder
				.addCase(saveWorkerFormAnswers.pending, (state) => {
					state.selfEval.saveStatus = "pending"
					state.selfEval.saveError = null
				})
				.addCase(saveWorkerFormAnswers.fulfilled, (state) => {
					state.selfEval.saveStatus = "fulfilled"
				})
				.addCase(saveWorkerFormAnswers.rejected, (state, action: any) => {
					state.selfEval.saveStatus = "rejected"
					state.selfEval.saveError =
						(action.payload as string) ?? "Failed to save answers"
				})
	},
})

export const {
	openEvaluationsPage,
	closeEvaluationsPage,
	openFormDetail,
	openProgramDetail,
	goBackToList,
	goBackToProgramDetail,
	resetAcknowledgeFormStatus,
	resetDeclineFormStatus,
	openSelfEvalProgram,
	openSelfEvalForm,
	closeSelfEvalForm,
	closeSelfEvalProgram,
	resetSelfEvalSaveStatus,
} = workerEvaluationsSlice.actions

export default workerEvaluationsSlice.reducer
